import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowRight, SpinnerGap, Warning } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { myApi } from "@/my/api";
import { captureReferFromUrl, setReferCookie } from "@/shared/auth/referCookie";
import {
  parseItemsParam, setPendingCart, claimPendingCart,
  setBackUrl, resolveBackUrl, setPostAuthTarget,
} from "@/shared/auth/pendingCart";

/**
 * Landing externo invocado pelo WordPress (1 produto de cada vez):
 *   `/cart/intent?item=ID:QTY&back=https://shop.../produto-X&REFER=123`
 *
 * Fluxo:
 *  - Logado → adiciona os items ao carrinho e segue para `/my/carrinho`.
 *  - Sem login → guarda os items em sessionStorage e redirecciona para
 *    `/login?next=/my/carrinho`. O `LoginPage`/`RegisterPage` fazem o
 *    claim automático após autenticação bem sucedida.
 *
 * Suporta também a sintaxe legacy `?items=ID:QTY,ID:QTY` (multi-produto).
 */
export default function CartIntentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  // `claimError` é o único state runtime — todos os outros estados (sem
  // items, a redireccionar, a fazer claim) são derivados de props.
  const [claimError, setClaimError] = useState("");
  // Guard contra double-effect (React StrictMode dev) — garante 1 só claim.
  const claimedRef = useRef(false);

  // Items parsed do URL — sincronamente derivado (sem state).
  const items = useMemo(() => {
    const raw = searchParams.get("item") || searchParams.get("items");
    return parseItemsParam(raw);
  }, [searchParams]);

  useEffect(() => {
    // Side-effects independentes do auth: back URL + REFER cookie.
    setBackUrl(resolveBackUrl(searchParams));
    const referFromUrl = searchParams.get("REFER") || searchParams.get("refer");
    if (referFromUrl) {
      const id = parseInt(referFromUrl, 10);
      if (Number.isInteger(id) && id > 0) setReferCookie(id);
    } else {
      captureReferFromUrl();
    }

    if (!items.length || authLoading) return;

    if (user) {
      if (claimedRef.current) return;
      claimedRef.current = true;
      setPendingCart(items);
      claimPendingCart(myApi).then(({ added, failed }) => {
        if (added === 0) {
          setClaimError(`Não foi possível adicionar ao carrinho${failed ? " (todos falharam)" : ""}.`);
          return;
        }
        navigate("/my/carrinho", { replace: true });
      });
      return;
    }

    // Sem login → guarda items + alvo e redirecciona para /login.
    setPendingCart(items);
    setPostAuthTarget("/my/carrinho");
    navigate("/login?next=/my/carrinho", { replace: true });
  }, [items, searchParams, user, authLoading, navigate]);

  // ── Renders ──
  const errorMsg =
    !items.length ? "Não foram especificados produtos para adicionar ao carrinho."
    : claimError || "";

  if (errorMsg) {
    return (
      <div className="min-h-screen bg-[#F5F5F2] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center" data-testid="cart-intent-error">
          <Warning size={48} weight="fill" className="mx-auto mb-3 text-amber-500" />
          <h2 className="text-xl font-bold text-[#0F1829] mb-2">Não foi possível continuar</h2>
          <p className="text-sm text-[#6B7280] mb-6">{errorMsg}</p>
          <Link to="/my" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F1829] text-white rounded-md text-sm font-medium hover:bg-[#001f6e]">
            Ir para o portal <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  const label =
    authLoading ? "A preparar carrinho..."
    : user ? "A adicionar ao carrinho..."
    : "A preparar a sua sessão...";

  return (
    <div className="min-h-screen bg-[#F5F5F2] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center" data-testid="cart-intent-loading">
        <SpinnerGap size={48} className="animate-spin mx-auto mb-4 text-[#0F1829]" />
        <p className="text-sm text-[#6B7280]">{label}</p>
      </div>
    </div>
  );
}
